import type {
  FieldDefinition,
  FieldOption,
  FieldType,
  FormLayout,
  FormSystemField,
  ProjectFieldBinding,
  ResolvedForm,
} from '@servicetim/shared';
import type { SchemaFormField, SchemaFormOption, SchemaFormSection } from './types';

/** How the six system fields a form may place render (`FORM_SYSTEM_FIELDS` in shared). */
export const FORM_SYSTEM_FIELD_TYPES: Record<FormSystemField, FieldType> = {
  summary: 'text',
  description: 'long_text',
  priority_id: 'single_select',
  assignee_id: 'user',
  labels: 'tags',
  due_at: 'datetime',
};

export const FORM_SYSTEM_FIELD_LABELS: Record<FormSystemField, string> = {
  summary: 'Summary',
  description: 'Description',
  priority_id: 'Priority',
  assignee_id: 'Assignee',
  labels: 'Labels',
  due_at: 'Due',
};

export const isFormSystemField = (key: string): key is FormSystemField =>
  key in FORM_SYSTEM_FIELD_TYPES;

export interface NormalizeExtras {
  /** Options for `priority_id` (from `usePriorities`); the API does not inline them. */
  priorities?: SchemaFormOption[];
}

/**
 * Sections for a resolved form (`GET …/forms/{id}/resolved`). With the form's `layout` the
 * fields keep their sections; without it everything lands in one section. Fields the server
 * left out (no read access) are skipped.
 */
export function sectionsFromResolvedForm(
  form: ResolvedForm,
  layout?: FormLayout,
  extras: NormalizeExtras = {},
): SchemaFormSection[] {
  const byKey = new Map(form.fields.map((f) => [f.key, f]));
  const toField = (key: string): SchemaFormField | null => {
    const f = byKey.get(key);
    if (!f) return null;
    return {
      key: f.key,
      type: f.type,
      label: f.label,
      required: f.required,
      readOnly: f.readOnly,
      system: f.system,
      visibleWhen: f.visibleWhen,
      options: f.options ?? (f.key === 'priority_id' ? extras.priorities : undefined),
    };
  };

  if (layout) {
    return layout.sections
      .map((section) => ({
        title: section.title,
        description: section.description,
        fields: section.fields
          .map((f) => toField(f.key))
          .filter((f): f is SchemaFormField => f !== null),
      }))
      .filter((section) => section.fields.length > 0);
  }
  return [
    {
      fields: form.fields
        .map((f) => toField(f.key))
        .filter((f): f is SchemaFormField => f !== null),
    },
  ];
}

export interface BindingsOptions extends NormalizeExtras {
  /** Keep only bindings that apply to this issue type (`issueTypeIds` null = all). */
  issueTypeId?: string | null;
  /** Definitions (for `config`: max length, unit, precision, pickers' restrictions). */
  definitions?: readonly FieldDefinition[];
  /** Options per field id (`useFieldOptions`); archived ones are dropped here. */
  optionsByFieldId?: Record<string, readonly FieldOption[]>;
  /** System fields to prepend in their own section (e.g. `['summary', 'description', 'priority_id']`). */
  systemFields?: readonly FormSystemField[];
  /** Section titles. */
  systemTitle?: string;
  customTitle?: string;
}

/**
 * Sections for a project's bindings (`GET …/field-bindings`), used when no form exists yet
 * (e.g. the bindings preview in the field builder, or a fallback create form).
 */
export function sectionsFromBindings(
  bindings: readonly ProjectFieldBinding[],
  options: BindingsOptions = {},
): SchemaFormSection[] {
  const definitions = new Map((options.definitions ?? []).map((d) => [d.id, d]));
  const applicable = bindings
    .filter(
      (b) =>
        options.issueTypeId === undefined ||
        options.issueTypeId === null ||
        b.issueTypeIds === null ||
        b.issueTypeIds.includes(options.issueTypeId),
    )
    .slice()
    .sort((a, b) => a.position - b.position);

  const custom: SchemaFormField[] = applicable.map((b) => {
    const definition = definitions.get(b.fieldId);
    return {
      key: b.fieldKey,
      type: b.fieldType,
      label: b.fieldLabel,
      required: b.required,
      readOnly: false,
      system: false,
      description: definition?.description ?? null,
      config: definition?.config,
      options: options.optionsByFieldId?.[b.fieldId]
        ?.filter((o) => o.archivedAt === null)
        .map((o) => ({ id: o.id, label: o.label, color: o.color })),
    };
  });

  const sections: SchemaFormSection[] = [];
  if (options.systemFields && options.systemFields.length > 0) {
    sections.push({
      title: options.systemTitle,
      fields: options.systemFields.map((key) => systemField(key, options)),
    });
  }
  if (custom.length > 0) sections.push({ title: options.customTitle, fields: custom });
  return sections;
}

/** A system field row with sensible defaults (`summary` required). */
export function systemField(key: FormSystemField, extras: NormalizeExtras = {}): SchemaFormField {
  return {
    key,
    type: FORM_SYSTEM_FIELD_TYPES[key],
    label: FORM_SYSTEM_FIELD_LABELS[key],
    required: key === 'summary',
    readOnly: false,
    system: true,
    options: key === 'priority_id' ? extras.priorities : undefined,
  };
}

/** Every field of every section, in order. */
export const flattenSections = (sections: readonly SchemaFormSection[]): SchemaFormField[] =>
  sections.flatMap((s) => s.fields);
